// Mobile Mail — Detail view (Gmail Mobile Web 风格).
// Body recovery: if body_text / body_html_sanitized empty, call fetchBody once
// then re-getMessage (mirrors PC plan §C.1 state machine).
// HTML render: server-sanitized body_html_sanitized via dangerouslySetInnerHTML
// (no client-side re-sanitize — backend already sanitized).
// Auto mark-read on open (Gmail behavior).
// Reply init: passes {parentMessageId, to, subject} to onReply — same logic as PC.

import { useEffect, useState } from "react";
import { ArrowLeft, Paperclip, Reply } from "lucide-react";
import { useMessageAttachments } from "../../modules/mail-v2/hooks/useMessageAttachments";
import { mailV2Api, type MailMessageV2DTO } from "../../api/mail-v2";

export interface ReplyInit {
  parentMessageId: string;
  to: string;
  subject: string;
}

interface Props {
  messageId: string;
  onBack: () => void;
  onReply: (init: ReplyInit) => void;
}

type BodyFetchStatus = "loading" | "fetching" | "ready" | "empty";

function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ja-JP", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSize(bytes: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export const MobileMailDetail = ({ messageId, onBack, onReply }: Props) => {
  const [message, setMessage] = useState<MailMessageV2DTO | null>(null);
  const [bodyFetchStatus, setBodyFetchStatus] = useState<BodyFetchStatus>("loading");
  const { items: attachments } = useMessageAttachments(messageId, true);

  useEffect(() => {
    let cancelled = false;
    setMessage(null);
    setBodyFetchStatus("loading");

    (async () => {
      try {
        const m = await mailV2Api.getMessage(messageId);
        if (cancelled) return;
        setMessage(m);

        // Auto mark-read (Gmail behavior).
        if (!m.is_read) {
          mailV2Api.markRead(messageId, true).catch(() => undefined);
        }

        // Body presence check.
        const hasBody = !!(m.body_text?.trim() || m.body_html_sanitized?.trim());
        if (hasBody) {
          setBodyFetchStatus("ready");
          return;
        }

        // Empty body — try fetch-body (PC plan §C.1).
        setBodyFetchStatus("fetching");
        try {
          await mailV2Api.fetchBody(messageId);
          if (cancelled) return;
          const reloaded = await mailV2Api.getMessage(messageId);
          if (cancelled) return;
          setMessage(reloaded);
          const stillEmpty = !(
            reloaded.body_text?.trim() || reloaded.body_html_sanitized?.trim()
          );
          setBodyFetchStatus(stillEmpty ? "empty" : "ready");
        } catch {
          if (!cancelled) setBodyFetchStatus("empty");
        }
      } catch {
        if (!cancelled) setBodyFetchStatus("empty");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messageId]);

  const fileAttachments = attachments.filter((a) => !a.is_inline);
  const recipients = (message?.to_addresses ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const handleReply = () => {
    if (!message) return;
    onReply({
      parentMessageId: message.id,
      to: message.reply_to ?? message.from_email,
      subject: message.subject?.startsWith("Re:")
        ? message.subject
        : `Re: ${message.subject ?? ""}`,
    });
  };

  const renderBody = () => {
    if (bodyFetchStatus === "loading") {
      return <div className="mobile-mail-body-loading">読み込み中...</div>;
    }
    if (bodyFetchStatus === "fetching") {
      return <div className="mobile-mail-body-loading">本文を取得しています...</div>;
    }
    if (bodyFetchStatus === "empty" || !message) {
      return <div className="mobile-mail-body-empty">本文がありません</div>;
    }
    if (message.body_html_sanitized?.trim()) {
      return (
        <div
          className="mobile-mail-body"
          dangerouslySetInnerHTML={{ __html: message.body_html_sanitized }}
        />
      );
    }
    if (message.body_text?.trim()) {
      return (
        <div className="mobile-mail-body mobile-mail-body-text">
          {message.body_text}
        </div>
      );
    }
    return <div className="mobile-mail-body-empty">本文がありません</div>;
  };

  const senderName =
    message?.from_name?.trim() || message?.from_email || "(送信者不明)";
  const senderInitial = senderName.charAt(0).toUpperCase();

  return (
    <>
      <header className="mobile-appbar">
        <div className="mobile-appbar-left">
          <button
            type="button"
            className="mobile-appbar-icon-btn"
            onClick={onBack}
            aria-label="戻る"
          >
            <ArrowLeft size={20} />
          </button>
        </div>
        <h1 className="mobile-appbar-title">メール</h1>
        <div className="mobile-appbar-actions" aria-hidden="true" />
      </header>

      <main className="mobile-content">
        <div className="mobile-mail-detail">
          <h2 className="mobile-mail-detail-subject">
            {message?.subject || "(件名なし)"}
          </h2>

          <div className="mobile-mail-meta">
            <div className="mobile-mail-meta-avatar">{senderInitial}</div>
            <div className="mobile-mail-meta-main">
              <div className="mobile-mail-meta-sender">{senderName}</div>
              {message?.from_email && (
                <div className="mobile-mail-meta-email">
                  &lt;{message.from_email}&gt;
                </div>
              )}
              {recipients.length > 0 && (
                <div className="mobile-mail-meta-to">
                  宛先: {recipients.join(", ")}
                </div>
              )}
              <div className="mobile-mail-meta-date">
                {formatDateTime(message?.received_at ?? null)}
              </div>
            </div>
          </div>

          {renderBody()}

          {fileAttachments.length > 0 && (
            <div className="mobile-mail-attachments">
              <div className="mobile-mail-attachments-label">
                添付ファイル ({fileAttachments.length})
              </div>
              {fileAttachments.map((a) => (
                <a
                  key={a.id}
                  href={`/api/mail-v2/attachments/${a.id}/download?inline=0`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-mail-attachment-item"
                >
                  <Paperclip size={14} />
                  <span className="mobile-mail-attachment-name">{a.filename}</span>
                  <span className="mobile-mail-attachment-size">
                    {formatSize(a.size_bytes)}
                  </span>
                </a>
              ))}
            </div>
          )}

          <div className="mobile-mail-reply-actions">
            <button
              type="button"
              className="mobile-mail-reply-button"
              onClick={handleReply}
            >
              <Reply
                size={16}
                style={{ marginRight: 8, verticalAlign: "middle" }}
              />
              返信
            </button>
          </div>
        </div>
      </main>
    </>
  );
};
